import { motion, AnimatePresence } from "framer-motion";
import { 
  Mic, 
  Users, 
  FileText, 
  Lock, 
  Send,
  Clock,
  CheckCircle2,
  Smile,
  Moon,
  Languages,
  Database
} from "lucide-react";
import { useState, useEffect } from "react";

// Brand colors
const PRIMARY_BLUE = "#1356a6";
const ACCENT_COLOR = "#0AB89E";

const heroImage = "/assets/cards/abstract_soundwave_visualization_in_medical_setting.png";
const appScreen = "/assets/cards/tablet_showing_real-time_medical_transcription.png";

export default function AmbientScribeCard() {
  const features = [
    {
      icon: Mic,
      title: "Passive Voice Capture",
      desc: "Documenting happens in the background; providers stay present with patients"
    },
    {
      icon: Users,
      title: "Multi-Speaker Intelligence",
      desc: "Distinguishes provider, patient, and family voices with precision"
    },
    {
      icon: FileText,
      title: "Real-Time SOAP Generation",
      desc: "Notes structured to your specialty templates as the encounter unfolds"
    },
    {
      icon: Lock,
      title: "Smart Code Suggestions",
      desc: "ICD-10, SNOMED and CPT codes recommended based on listened scribing notes"
    },
    {
      icon: Send,
      title: "Auto-Delivered Summaries",
      desc: "Patient-friendly visit summaries sent via email or SMS post-encounter"
    }
  ];

  const kpis = [
    { label: "Documentation Time", value: "-70%", icon: Clock, color: "text-emerald-400" },
    { label: "Transcription Accuracy", value: "98%", icon: CheckCircle2, color: "text-blue-400" },
    { label: "Provider Satisfaction", value: "96%", icon: Smile, color: "text-pink-400" },
    { label: "After-Hours Charting", value: "-80%", icon: Moon, color: "text-purple-400" },
  ];

  const [currentKpi, setCurrentKpi] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentKpi((prev) => (prev + 1) % kpis.length);
    }, 3000);
    return () => clearInterval(timer);
  }, [kpis.length]);

  return (
    <div className="w-full h-full">
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="relative bg-white/80 backdrop-blur-xl border border-white/20 rounded-2xl overflow-hidden shadow-2xl grid lg:grid-cols-2 gap-0 h-[700px]"
      >
        {/* Left Column: Content */}
        <div className="p-6 md:p-8 flex flex-col justify-start relative z-10 overflow-hidden">
          <div className="mb-3 inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 text-sm font-bold uppercase tracking-wider w-fit" style={{ color: PRIMARY_BLUE }}>
            <span className="relative flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ backgroundColor: PRIMARY_BLUE }}></span>
              <span className="relative inline-flex rounded-full h-3 w-3" style={{ backgroundColor: PRIMARY_BLUE }}></span>
            </span>
            Ambient Listening
          </div>
          
          <h1 className="text-3xl md:text-4xl font-normal mb-2 tracking-tight" style={{ color: PRIMARY_BLUE }}>
            Ambient Listening & Scribe
          </h1>
          
          <h2 className="text-lg md:text-xl font-medium mb-6" style={{ color: `${PRIMARY_BLUE}cc` }}>
            Invisible AI. Visible Results.
          </h2>
          
          <p className="text-lg text-slate-500 mb-8 italic border-l-4 pl-5" style={{ borderColor: `${ACCENT_COLOR}80` }}>
            "Clinician conversations become structured documentation — automatically, accurately, instantly."
          </p>

          <div className="space-y-3">
            {features.map((feature, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 + (index * 0.1), duration: 0.5 }}
                className="flex gap-5 group"
              >
                <div className="flex-shrink-0">
                  <div 
                    className="w-12 h-12 rounded-xl bg-slate-50 flex items-center justify-center transition-colors duration-300 shadow-sm group-hover:text-white"
                    style={{ 
                      '--hover-bg': PRIMARY_BLUE 
                    } as React.CSSProperties}
                  >
                    <feature.icon className="w-6 h-6 transition-colors" style={{ color: ACCENT_COLOR }} />
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-lg transition-colors" style={{ color: PRIMARY_BLUE }}>
                    {feature.title}
                  </h3>
                  <p className="text-base text-slate-500 leading-relaxed">
                    {feature.desc}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Right Column: Visuals */}
        <div className="relative h-full overflow-hidden bg-slate-900 flex items-center justify-center">
          
          {/* Background Layer: Soundwave Flow */}
          <div className="absolute inset-0 z-0">
             <img 
              src={heroImage} 
              alt="Background Flow" 
              className="w-full h-full object-cover opacity-80"
            />
            <div className="absolute inset-0 bg-slate-900/30 mix-blend-multiply" />
          </div>

          {/* Floating Tablet with App Screen */}
          <motion.div 
            initial={{ opacity: 0, y: 40, rotateY: -10 }}
            animate={{ opacity: 1, y: 0, rotateY: 0 }}
            transition={{ delay: 0.4, duration: 0.8, ease: "easeOut" }}
            className="relative z-10 w-[75%] max-w-md aspect-[3/4] rounded-3xl overflow-hidden shadow-2xl -mt-8"
          >
             {/* Screen Content */}
             <img 
              src={appScreen} 
              alt="Tablet Scribe Interface" 
              className="w-full h-full object-cover"
            />
             
             {/* Screen Glare */}
             <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/10 to-transparent pointer-events-none z-20" />
             
             {/* Live Audio Visualizer Overlay (Simulated) */}
             <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-slate-900 via-slate-900/80 to-transparent p-6 flex flex-col justify-end">
               <div className="flex items-center gap-2 mb-2">
                 <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                 <span className="text-xs font-bold text-white uppercase tracking-wider">Listening • 12:45</span>
               </div>
               
               {/* Animated Waveform Bars */}
               <div className="flex items-end justify-between h-12 gap-1">
                 {[...Array(20)].map((_, i) => (
                   <motion.div
                     key={i}
                     animate={{ 
                       height: ["20%", `${Math.random() * 80 + 20}%`, "20%"] 
                     }}
                     transition={{ 
                       duration: 0.5 + Math.random() * 0.5, 
                       repeat: Infinity, 
                       ease: "easeInOut",
                       delay: Math.random() * 0.5
                     }}
                     className="w-1.5 rounded-full"
                     style={{ backgroundColor: `${ACCENT_COLOR}cc` }}
                   />
                 ))}
               </div>
             </div>
          </motion.div>

          {/* Floating Feature Cards */}
          <div className="absolute inset-0 z-20 pointer-events-none">
             {/* Speaker ID Card - Top Left */}
             <motion.div
               animate={{ y: [0, -10, 0], opacity: [0.8, 1, 0.8] }}
               transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 1 }}
               className="absolute top-[12%] left-[8%] bg-white/90 backdrop-blur text-slate-800 p-4 rounded-xl shadow-lg flex items-center gap-3"
             >
               <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600">
                 <Users className="w-5 h-5" />
               </div>
               <div>
                 <div className="text-xs font-bold text-slate-500">Speaker ID</div>
                 <div className="text-base font-bold text-slate-800">Dr. Sarah Chen</div>
               </div>
             </motion.div>

             {/* Multi-lingual Card - Top Right */}
             <motion.div
               animate={{ y: [0, 10, 0], opacity: [0.8, 1, 0.8] }}
               transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut", delay: 1.5 }}
               className="absolute top-[15%] right-[5%] bg-white/90 backdrop-blur text-slate-800 p-4 rounded-xl shadow-lg flex items-center gap-3"
             >
               <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center text-purple-600">
                 <Languages className="w-5 h-5" />
               </div>
               <div>
                 <div className="text-xs font-bold text-slate-500">Language</div>
                 <div className="text-base font-bold text-slate-800">Multi-lingual</div>
               </div>
             </motion.div>

             {/* Smart Coding Card - Middle Left */}
             <motion.div
               animate={{ y: [0, -8, 0], opacity: [0.8, 1, 0.8] }}
               transition={{ duration: 5.5, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
               className="absolute top-[42%] left-[3%] bg-white/90 backdrop-blur text-slate-800 p-4 rounded-xl shadow-lg flex items-center gap-3"
             >
               <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center text-amber-600">
                 <Database className="w-5 h-5" />
               </div>
               <div>
                 <div className="text-xs font-bold text-slate-500">Smart Coding</div>
                 <div className="text-base font-bold text-slate-800">ICD-10 Ready</div>
               </div>
             </motion.div>

             {/* Auto-Summary Card - Middle Right */}
             <motion.div
               animate={{ y: [0, -12, 0], opacity: [0.8, 1, 0.8] }}
               transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 2.5 }}
               className="absolute top-[40%] right-[3%] bg-white/90 backdrop-blur text-slate-800 p-4 rounded-xl shadow-lg flex items-center gap-3"
             >
               <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600">
                 <FileText className="w-5 h-5" />
               </div>
               <div>
                 <div className="text-xs font-bold text-slate-500">SOAP Generated</div>
                 <div className="text-base font-bold text-slate-800">Ready for Review</div>
               </div>
             </motion.div>
          </div>

          {/* Floating UI Cards (Context) - KPI Carousel */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-30 w-72 hidden md:block">
            <div className="relative h-20 perspective-1000">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentKpi}
                  initial={{ opacity: 0, y: 15, rotateX: 10 }}
                  animate={{ opacity: 1, y: 0, rotateX: 0 }}
                  exit={{ opacity: 0, y: -15, rotateX: -10 }}
                  transition={{ duration: 0.5, ease: "backOut" }}
                  className="absolute inset-0 backdrop-blur-md border border-white/20 px-4 py-3 rounded-xl flex items-center gap-4 overflow-hidden"
                  style={{
                    backgroundColor: `${PRIMARY_BLUE}e6`,
                    boxShadow: `0 10px 30px -10px ${PRIMARY_BLUE}99`
                  }}
                >
                   {/* Glare Effect */}
                   <div className="absolute inset-0 bg-gradient-to-tr from-white/10 to-transparent pointer-events-none" />

                  <div className="p-3 bg-white/10 rounded-lg backdrop-blur-sm border border-white/10 relative z-10">
                    {(() => {
                      const Icon = kpis[currentKpi].icon;
                      return <Icon className="w-6 h-6 text-white" />;
                    })()}
                  </div>
                  <div className="relative z-10 flex-1">
                    <div className="text-xs font-bold text-white/70 uppercase tracking-wider leading-tight">
                      {kpis[currentKpi].label}
                    </div>
                    <div className="text-2xl font-bold text-white drop-shadow-sm">
                      {kpis[currentKpi].value}
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
            
            {/* Carousel Indicators */}
            <div className="flex justify-center gap-1.5 mt-2">
              {kpis.map((_, idx) => (
                <div 
                  key={idx} 
                  className={`h-1 rounded-full transition-all duration-300 shadow-sm ${
                    idx === currentKpi ? "w-4" : "w-1.5 bg-slate-300/50"
                  }`}
                  style={{
                    backgroundColor: idx === currentKpi ? PRIMARY_BLUE : undefined
                  }}
                />
              ))}
            </div>
          </div>

        </div>
      </motion.div>
    </div>
  );
}

